import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import httpclient from "../../../../Utils";
import { buildApiQuery } from "../../../../Utils/apiQueryBuilder";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
// Old flat schema (superseded below by the grouped-by-customer endpoint) -
// kept only as an import reference, no longer called.
// import { resolveOrdersEndpoint } from "../Orders/orderFilterConfig";
import OrderCounts from "../Orders/OrderCounts";
import OrderDetailsModal from "../Orders/OrderDetailsModal";
import FilterBtn from "../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../Assets/Icons/btn-filters-up.png";
import Filter from "./Filter";
import {
  INITIAL_FILTER_STATE,
  fieldMappings,
  rangeFields,
  valueTransformers,
} from "./filterConfig";
import JobsTable from "./JobsTable";
import FileViewerDialog from "./FileViewerDialog";

// New "Today's Jobs" page built to match the client's updated requirement.
// Kept separate from the existing Delivery/Orders "Today Jobs" page (menu id
// 66), which stays untouched - backend will remove that old menu link later.
// Reuses the same OrderCounts component/pattern.
//
// Job listing uses a separate, grouped-by-customer endpoint
// (/delivery/new-today-orders/{code}) instead of the old flat
// /delivery/today-orders/{code} (still used by the old Orders page via
// resolveOrdersEndpoint - untouched). Each item in the new response's
// data[] is itself a full job (same fields as before) plus
// grouped_invoice_count/associated_jobs when multiple jobs for the same
// customer got bundled together - those render as a collapsible group in
// JobsTable instead of one row per job. Filters/sorting keep the exact
// same query-param format (buildApiQuery + fieldMappings) as before.
const resolveNewTodayOrdersEndpoint = (tabValue) =>
  `/delivery/new-today-orders/${tabValue || "all"}`;

const TodaysJobsNew = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "overview");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [viewerState, setViewerState] = useState({ open: false, files: [], title: "" });
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedInvoiceNumbers, setSelectedInvoiceNumbers] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [shiftModes, setShiftModes] = useState([]);
  const [allowAllInvoiceTab, setAllowAllInvoiceTab] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
  const [sortModel, setSortModel] = useState({ field: null, direction: "asc" });

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchOrders = async () => {
    setLoading(true);
    // const endpoint = resolveOrdersEndpoint(activeTab, true); // old flat schema
    const endpoint = resolveNewTodayOrdersEndpoint(activeTab);
    const apiUrl = buildApiQuery(endpoint, {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      filters: submittedData,
      sortModel: sortModel.field
        ? [{ field: sortModel.field, sort: sortModel.direction }]
        : [],
      fieldMappings,
      rangeFields,
      valueTransformers,
    });

    try {
      const res = await httpclient.get(apiUrl);
      const { data } = res;

      if (data.status === 200) {
        const rows = (data.data || []).map((order, index) => {
          const driverCode = order.driver_code || order.assigned_driver;
          const groupedInvoiceCount = order.grouped_invoice_count || 1;

          return {
            id: order.job_id || index,
            jobNumber: order.job_id,
            customerCode: order.customer_code,
            deliveryDate: order.delivery_date,
            driverCode,
            customerName: order.customer,
            address: order.customer_address,
            shift: order.shift_mode,
            podTime: order.pod_time,
            jobStatus: order.delivery_status,
            jobStatusColor: order.delivery_status_color,
            rowColor: order.row_color,
            signatureFiles: order.signature_files || [],
            otherFiles: order.other_files || [],
            invoiceNumber: order.invoice_number,
            relatedJobList: order.related_job_list || [],
            // Multiple jobs for the same customer come bundled here - shown
            // as a collapsible group in JobsTable when there's more than one.
            groupedInvoiceCount,
            // associated_jobs includes the parent job itself as one of its
            // entries - exclude it here so expanding doesn't show it twice
            // (once as the main row, once again as a child).
            associatedJobs: (order.associated_jobs || [])
              .filter((job) => job.job_id !== order.job_id)
              .map((job, childIndex) => ({
              id: `${order.job_id || index}-child-${job.job_id || childIndex}`,
              jobNumber: job.job_id,
              customerCode: job.customer_code,
              deliveryDate: job.delivery_date,
              driverCode,
              customerName: job.customer,
              address: job.customer_address,
              shift: job.shift_mode,
              podTime: job.pod_time,
              jobStatus: job.delivery_status,
              jobStatusColor: job.delivery_status_color,
              signatureFiles: job.signature_files || [],
              otherFiles: job.other_files || [],
              invoiceNumber: job.invoice_number,
              relatedJobList: [],
            })),
          };
        });
        setOrders(rows);
        setRowCount(data.meta?.total || data.count || 0);

        if (data.drivers && data.drivers.length > 0) {
          setDrivers(data.drivers);
        }
        if (data.orderStatus && data.orderStatus.length > 0) {
          setOrderStatuses(data.orderStatus);
        }
        if (data.shift_modes && data.shift_modes.length > 0) {
          setShiftModes(data.shift_modes);
        }
        setAllowAllInvoiceTab(data.allow_all_invoice_tab_enabled === 1);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, paginationModel.page, paginationModel.pageSize, submittedData, sortModel]);

  // Clear the dashboard-provided nav state once applied, so a later in-page
  // tab click (or navigating back) doesn't get overridden by it again.
  useEffect(() => {
    if (location.state?.activeTab) {
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardClick = (_, tabValue) => {
    setActiveTab(tabValue);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // Status filter only applies to "overview" and "all" - clear it when
    // leaving both so a stale value doesn't silently keep filtering other
    // status tabs.
    if (tabValue !== "overview" && tabValue !== "all") {
      setFilterData((prev) => ({ ...prev, status: "" }));
      setSubmittedData((prev) => {
        const { status, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilterToggle = (event) => {
    setFilterAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSubmittedData(filterData);
    handleFilterClose();
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilterClose();
  };

  const handleRemove = (field) => {
    setFilterData((prev) => ({ ...prev, [field]: "" }));
    setSubmittedData((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortChange = (field) => {
    setSortModel((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (_, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) });
  };

  const handleOpenFiles = (files, title) => {
    const validFiles = (files || []).filter((file) => file?.file_path || typeof file === "string");
    if (!validFiles.length) return;
    setViewerState({ open: true, files: validFiles, title });
  };

  const handleCloseFiles = () => {
    setViewerState({ open: false, files: [], title: "" });
  };

  // Same row-click behavior as the old Today Jobs page: prefer
  // related_job_list (grouped invoices) if present, else fall back to the
  // row's own invoice_number.
  const handleRowClick = (row) => {
    setSelectedJobId(row.jobNumber);

    if (row.relatedJobList && row.relatedJobList.length > 0) {
      setSelectedInvoiceNumbers(row.relatedJobList.join(","));
      setOrderDetailsOpen(true);
      return;
    }

    if (row.invoiceNumber) {
      setSelectedInvoiceNumbers(row.invoiceNumber);
      setOrderDetailsOpen(true);
    } else {
      showNotification("error", "No invoice number found for this order");
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedJobId(null);
    setSelectedInvoiceNumbers("");
  };

  return (
    <Box sx={{ p: 3 }}>
      <OrderCounts
        isToday
        showAllCard={allowAllInvoiceTab}
        showWaitingCard={allowAllInvoiceTab}
        activeTab={activeTab}
        onCardClick={handleCardClick}
      />

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Jobs
            </Typography>
            <Box
              component="img"
              src={filterAnchorEl ? FilterBtnUp : FilterBtn}
              alt="Filter"
              sx={{ cursor: "pointer" }}
              onClick={handleFilterToggle}
            />
          </Box>
        </Grid>
        <Filter
          filterOpen={Boolean(filterAnchorEl)}
          filterAnchorEl={filterAnchorEl}
          onCloseFilter={handleFilterClose}
          filterData={filterData}
          handleChangeFilter={handleChangeFilter}
          handleFilter={onFilterClick}
          handleClearFilters={handleClearFilters}
          handleRemove={handleRemove}
          submittedData={submittedData}
          drivers={drivers}
          activeTab={activeTab}
          orderStatuses={orderStatuses}
          shiftModes={shiftModes}
        />
      </Grid>

      <JobsTable
        rows={orders}
        loading={loading}
        rowCount={rowCount}
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onOpenFiles={handleOpenFiles}
        onRowClick={handleRowClick}
        sortModel={sortModel}
        onSortChange={handleSortChange}
      />

      <FileViewerDialog
        open={viewerState.open}
        onClose={handleCloseFiles}
        files={viewerState.files}
        title={viewerState.title}
      />

      {orderDetailsOpen && (
        <OrderDetailsModal
          open={orderDetailsOpen}
          onClose={handleCloseOrderDetails}
          invoiceNumbers={selectedInvoiceNumbers}
          jobId={selectedJobId}
          allDrivers={[]}
          onRefresh={fetchOrders}
        />
      )}
    </Box>
  );
};

export default TodaysJobsNew;
